//CSS and assets
import './ChooseLocation.css';
import SettingsService from '../../Core/Services/SettingsService';

// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import LocationMap from './LocationMap';
import LocationItem from './LocationItem';
import FormLayout from '../ReusableComponents/FormLayout';

// Global state and services
import { useGlobal } from '../../Core/GlobalState/Store';
import { useStep } from '../Base/StepContext';
import LocationService from '../../Core/Services/LocationService';

export default function ChooseLocation() {
  const { t } = useTranslation();
  const { handleNext } = useStep();

  //Local State
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [error, setError] = useState(null);

  //GLOBAL STATE
  const { currentLocation, setCurrentLocation, setTitanEmail } = useGlobal();

  //Get Locations from locationService on component mount, handle errors and empty states
  useEffect(() => {
    try {
      const formattedLocations = LocationService.getActiveLocations();

      // If no locations are available, show an error message.
      if (formattedLocations.length === 0) {
        setError(t('error_no_locations_available'));
        return;
      }

      setLocations(formattedLocations);
      setFilteredLocations(formattedLocations);
    } catch (err) {
      setError(t('error_loading_locations'));
    }
  }, []);

  //handle filtering locations by postal code - for now this is just a placeholder that resets the list to all locations
  const handleFilterLocations = postalCode => {
    // We reset all if there search is empty
    if (postalCode === '' || postalCode.length < 3) {
      setFilteredLocations(locations);
      return;
    }

    // then we would filter based on the postal code, but since this is a placeholder, we skip that step for now.
    let filtered = locations
      .filter(loc =>
        loc.address.toLowerCase().includes(postalCode.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));
    setFilteredLocations(filtered);
  };

  // Handle location selection — update global state and move to next step
  const handleSelectLocation = locationId => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;

    // If location is not active we redirect to the form page
    if (!location.isActive) {
      const formPage = SettingsService.getFormPage();
      const container = location?.page_id ?? '';
      window.location.href = `${window.location.origin}/${formPage}/?type=self_storage&container=${container}`;
    } else {
      setCurrentLocation(location);
      setTitanEmail(location.email);
      handleNext();
    }
  };

  return (
    <FormLayout title="choose_location_title">
      <div className="md:grid grid-cols-1 flex flex-col-reverse md:grid-cols-2 !border !border-solid border-gray-400 !border-1">
        <div className="w-full md:min-h-[500px]">
          {error ? (
            <div className="p-6 text-center">
              <div className="bg-red-50 !border !border-solid border-red-200 rounded-lg p-4 text-red-700">
                {error}
              </div>
            </div>
          ) : (
            <div>
              <div className="p-4">
                <h2 className="text-sm mb-1 text-gray-800">
                  {t('search_location_by_postal')}
                </h2>
                <div>
                  <input
                    type="text"
                    className="text-gray-500 border-gray-500"
                    onChange={e => handleFilterLocations(e.target.value)}
                    placeholder={t('postal_code_placeholder')}
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[500px]">
                {filteredLocations.map(location => (
                  <LocationItem
                    key={location.id}
                    onSelect={handleSelectLocation}
                    location={location}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <LocationMap
          locations={filteredLocations}
          selectedLocation={currentLocation}
        />
      </div>
    </FormLayout>
  );
}
